import { portalFetch, type Portal } from '../auth/portalSession';
import { getApiBaseUrl } from './config';

export interface TimeSlot {
  time: string;
  isAvailable: boolean;
  intervalMinutes?: number;
}

export interface AvailableDate {
  date: string;
  dayName: string;
  availableSlots: number;
  totalSlots: number;
}

export interface AppointmentData {
  appointmentId?: string;
  fullName: string;
  phoneNumber: string;
  idCard: string;
  village: string;
  provider: string;
  serviceType: number;
  serviceId: string;
  serviceName: string;
  selectedDay: string;
  selectedDayLabel: string;
  selectedTime: string;
  intervalMinutes?: number;
  status: 'draft' | 'completed';
  createdBy?: string;
  clinicName?: string;
  isSynced?: boolean;
}

export const getAppointmentsByIdCard = async (
  idCard: string,
  serviceId?: string,
  futureOnly: boolean = true,
  portal: Portal = 'secretary'
): Promise<AppointmentData[]> => {
  const params = new URLSearchParams({
    idCard,
    futureOnly: futureOnly.toString()
  });
  
  if (serviceId) {
    params.append('serviceId', serviceId);
  }

  const response = await portalFetch(portal, `${getApiBaseUrl()}/appointments/by-idcard?${params}`);
  if (!response.ok) {
    throw new Error('Failed to get appointments by ID card');
  }
  
  return response.json();
};

export const checkDuplicateAppointment = async (
  idCard: string,
  serviceId: string,
  portal: Portal
): Promise<AppointmentData | null> => {
  // Use new efficient endpoint that queries by ID card
  const appointments = await getAppointmentsByIdCard(idCard, serviceId, true, portal);
  
  // Filter for completed appointments only
  const completedAppointments = appointments.filter(apt => apt.status === 'completed');
  
  // Return first matching completed appointment (they're sorted by date)
  return completedAppointments.length > 0 ? completedAppointments[0] : null;
};

export const getAvailableDatesApi = async (
  serviceType: number,
  serviceId?: string
): Promise<AvailableDate[]> => {
  // Use serviceType parameter (backend supports both serviceType and scheduleType)
  const params = new URLSearchParams({
    serviceType: serviceType.toString(),
  });
  
  if (serviceId) {
    params.append('serviceId', serviceId);
  }

  const response = await portalFetch('secretary', `${getApiBaseUrl()}/available-dates?${params}`);
  if (!response.ok) {
    throw new Error('Failed to fetch available dates');
  }
  const data = await response.json();
  return data.dates || data;
};

export const getAvailableSlotsApi = async (
  date: string,
  serviceType: number,
  serviceId?: string
): Promise<TimeSlot[]> => {
  // Use serviceType parameter (backend supports both serviceType and scheduleType)
  const params = new URLSearchParams({
    date,
    serviceType: serviceType.toString(),
  });
  
  if (serviceId) {
    params.append('serviceId', serviceId);
  }
  
  const response = await portalFetch('secretary', `${getApiBaseUrl()}/available-slots?${params}`);
  if (!response.ok) {
    throw new Error('Failed to fetch available slots');
  }
  const data = await response.json();
  return data.slots || data;
};

export const createAppointmentApi = async (data: AppointmentData): Promise<any> => {
  const response = await portalFetch('secretary', `${getApiBaseUrl()}/appointments`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  
  if (!response.ok) {
    throw new Error('Failed to create appointment');
  }
  
  return response.json();
};

export const updateAppointmentSyncStatusApi = async (
  appointmentId: string,
  isSynced: boolean
): Promise<any> => {
  const response = await portalFetch('secretary', `${getApiBaseUrl()}/appointments/${appointmentId}/sync`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ isSynced }),
  });
  
  if (!response.ok) {
    throw new Error('Failed to update sync status');
  }
  
  return response.json();
};

export const getAppointmentsByClinicApi = async (clinicName: string): Promise<any[]> => {
  const params = new URLSearchParams({
    clinicName,
  });
  
  const response = await portalFetch('secretary', `${getApiBaseUrl()}/appointments?${params}`);
  
  if (!response.ok) {
    throw new Error('Failed to fetch appointments');
  }
  
  return response.json();
};

export const deleteAppointmentApi = async (appointmentId: string): Promise<void> => {
  const response = await portalFetch('secretary', `${getApiBaseUrl()}/appointments/${appointmentId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    throw new Error('Failed to delete appointment');
  }
};
